import { accessTokenFromOidcStorage } from '../../utils/oidc-session';
export class AgentOfflineError extends Error {
    constructor(cause) {
        super(`The jaen agent could not be reached: ${String(cause)}`);
        this.name = 'AgentOfflineError';
    }
}
export class AgentError extends Error {
    constructor(message, code, details) {
        super(message);
        this.name = 'AgentError';
        this.code = code;
        this.details = details ?? undefined;
    }
}
const bearer = () => {
    try {
        return (accessTokenFromOidcStorage(sessionStorage.getItem(`oidc.user:${__JAEN_ZITADEL_GQL__.authority}:${__JAEN_ZITADEL_GQL__.clientId}`)) || undefined);
    }
    catch {
        return undefined;
    }
};
const request = async (config, query, variables) => {
    const token = bearer();
    let response;
    try {
        response = await fetch(config.url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({ query, variables })
        });
    }
    catch (error) {
        throw new AgentOfflineError(error);
    }
    if (response.status >= 500) {
        throw new AgentOfflineError(`HTTP ${response.status}`);
    }
    let body;
    try {
        body = await response.json();
    }
    catch (error) {
        throw new AgentOfflineError(error);
    }
    if (body.errors?.length) {
        const first = body.errors[0];
        throw new AgentError(body.errors.map(e => e.message).join('; '), first?.extensions?.code, first?.extensions?.details);
    }
    if (!body.data) {
        throw new AgentError(`The jaen agent answered no data (HTTP ${response.status})`);
    }
    return body.data;
};
const DRAFT = `query JaenAgentDraft($site: String!, $sinceRevision: Number) {
  draft(site: $site, sinceRevision: $sinceRevision) {
    site
    revision
    publishedRevision
    changed
    full
    delta {
      pages
      media
      removedMedia
      site
      widgets
      authors
      mediaField {
        pageId
        fieldType
        fieldName
      }
    }
    discardedRevision
    discardedAt
    discardedBy
    discardedByName
    readAt
  }
}`;
const DISCARD_PREVIEW = `query JaenAgentDiscardPreview($site: String!) {
  discardPreview(site: $site) {
    site
    revision
    publishedRevision
    canDiscard
    reason
    pages
    fields
    pagesAdded
    pagesRemoved
    mediaAdded
    mediaRemoved
    siteChanged
    widgetsChanged
    editors {
      sub
      name
      at
    }
    since
    publishedAt
    takenAt
  }
}`;
const DISCARD = `mutation JaenAgentDiscard($site: String!, $atRevision: Number) {
  discard(site: $site, atRevision: $atRevision) {
    site
    discarded
    revision
    previousRevision
    publishedRevision
    pages
    fields
    editors {
      sub
      name
      at
    }
    snapshotRevision
    snapshotAt
    snapshotBytes
    by {
      sub
      name
      at
    }
    at
    reason
  }
}`;
const SAVE = `mutation JaenAgentSave($site: String!, $changes: [SaveChangesInput!]!, $baseRevision: Number) {
  save(site: $site, changes: $changes, baseRevision: $baseRevision) {
    revision
    savedAt
    rebased
    overwrote {
      field
    }
  }
}`;
const VIEWER = `query JaenAgentViewer($site: String!) {
  viewer(site: $site) {
    site
    sub
    name
    at
  }
}`;
const SUBSCRIBE = `mutation JaenAgentSubscribe($site: String!) {
  subscribe(site: $site) {
    site
    ticket
    url
    expiresAt
    revision
  }
}`;
const PUBLISH = `mutation JaenAgentPublish($site: String!, $message: String) {
  publish(site: $site, message: $message) {
    published
    revision
    publishedRevision
    migrationUrl
    commitUrl
    publishedAt
    queued
    workflow
    runUrl
    reason
  }
}`;
export const fetchDraft = async (config, sinceRevision) => {
    const data = await request(config, DRAFT, {
        site: config.site,
        sinceRevision: typeof sinceRevision === 'number' ? sinceRevision : null
    });
    return data.draft;
};
const forTheWire = (changes) => changes.map(change => change.value === undefined ? { ...change, value: {} } : change);
export const saveChanges = async (config, changes, baseRevision) => {
    const data = await request(config, SAVE, {
        site: config.site,
        changes: forTheWire(changes),
        baseRevision: typeof baseRevision === 'number' ? baseRevision : null
    });
    return data.save;
};
export const warmAuth = async (config) => {
    try {
        const data = await request(config, VIEWER, {
            site: config.site
        });
        return data.viewer;
    }
    catch (error) {
        console.debug('jaen agent: the warm up call did not come back', error);
        return null;
    }
};
export const publishSite = async (config, message) => {
    const data = await request(config, PUBLISH, {
        site: config.site,
        message: message || null
    });
    return data.publish;
};
export const fetchDiscardPreview = async (config) => {
    const data = await request(config, DISCARD_PREVIEW, { site: config.site });
    return data.discardPreview;
};
export const discardDraft = async (config, atRevision) => {
    const data = await request(config, DISCARD, {
        site: config.site,
        atRevision: typeof atRevision === 'number' ? atRevision : null
    });
    return data.discard;
};
export const subscribeDraft = async (config) => {
    const data = await request(config, SUBSCRIBE, {
        site: config.site
    });
    return data.subscribe;
};
export const draftSocketUrl = (config) => {
    const base = config.socketUrl || config.url;
    if (!base)
        return null;
    try {
        const url = new URL(base, typeof location === 'undefined' ? undefined : location.href);
        url.protocol =
            url.protocol === 'http:'
                ? 'ws:'
                : url.protocol === 'https:'
                    ? 'wss:'
                    : url.protocol;
        if (!config.socketUrl) {
            url.pathname = url.pathname.replace(/\/graphql\/?$/, '') + '/draft';
        }
        url.pathname = `${url.pathname.replace(/\/$/, '')}/${encodeURIComponent(config.site)}`;
        url.search = '';
        return url.toString();
    }
    catch {
        return null;
    }
};
const SOCKET_RETRY_MS = [1000, 2000, 5000, 10000, 30000];
export const openDraftSocket = (config, handlers) => {
    if (typeof WebSocket === 'undefined') {
        return () => undefined;
    }
    let socket;
    let retryTimer;
    let attempts = 0;
    let stopped = false;
    let live = false;
    let opening = false;
    const setLive = (next) => {
        if (live === next)
            return;
        live = next;
        handlers.onLive(next);
    };
    const scheduleRetry = () => {
        if (stopped || retryTimer)
            return;
        const wait = SOCKET_RETRY_MS[Math.min(attempts, SOCKET_RETRY_MS.length - 1)] || 30000;
        attempts += 1;
        retryTimer = setTimeout(() => {
            retryTimer = undefined;
            void open();
        }, wait);
    };
    const open = async () => {
        if (stopped || socket || opening)
            return;
        opening = true;
        let ticket;
        try {
            ticket = await subscribeDraft(config);
        }
        catch (error) {
            console.debug('jaen agent: no draft ticket, the poll carries it', error);
            opening = false;
            scheduleRetry();
            return;
        }
        if (stopped) {
            opening = false;
            return;
        }
        const url = ticket.url || draftSocketUrl(config);
        if (!url || !ticket.ticket) {
            opening = false;
            scheduleRetry();
            return;
        }
        let next;
        try {
            next = new WebSocket(url, ['jaen-draft.v1', `ticket.${ticket.ticket}`]);
        }
        catch (error) {
            console.debug('jaen agent: the draft socket did not open', error);
            opening = false;
            scheduleRetry();
            return;
        }
        socket = next;
        opening = false;
        next.onopen = () => {
            attempts = 0;
            setLive(true);
        };
        next.onmessage = event => {
            let payload;
            try {
                payload = JSON.parse(String(event.data));
            }
            catch {
                return;
            }
            if (payload && typeof payload.revision === 'number') {
                handlers.onRevision(payload.revision, typeof payload.publishedRevision === 'number'
                    ? payload.publishedRevision
                    : undefined);
            }
        };
        next.onerror = () => {
        };
        next.onclose = () => {
            if (socket === next)
                socket = undefined;
            setLive(false);
            scheduleRetry();
        };
    };
    void open();
    return () => {
        stopped = true;
        setLive(false);
        if (retryTimer) {
            clearTimeout(retryTimer);
            retryTimer = undefined;
        }
        if (socket) {
            const closing = socket;
            socket = undefined;
            closing.onclose = null;
            closing.onmessage = null;
            closing.onerror = null;
            closing.onopen = null;
            try {
                closing.close();
            }
            catch {
            }
        }
    };
};
export const agentConfig = () => {
    const raw = typeof __JAEN_AGENT__ === 'undefined' ? undefined : __JAEN_AGENT__;
    if (!raw?.url || !raw?.site) {
        return null;
    }
    return {
        url: raw.url,
        site: raw.site,
        socketUrl: raw.socketUrl,
        pollMs: raw.pollMs || 5000,
        activePollMs: raw.activePollMs || 1500,
        debounceMs: raw.debounceMs || 1000
    };
};
